import axios from 'axios';

// Use external API for both development and production
const API_BASE = 'https://api.rigelworld.com/api';

// External API base URL
const EXTERNAL_API_BASE =  'https://api.rigelworld.com/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 10000, // 10 seconds timeout for better reliability
  headers: {
    'Content-Type': 'application/json',
  },
});

// Simple in-memory cache for API responses
const apiCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Add request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

/**
 * Get members/speakers by username
 * @param {string} username - The username to get members for
 * Returns list of speakers/members for the conference
 */
export const getMembersByUser = async (username) => {
  const cacheKey = `members_${username}`;
  
  // Check cache first
  const cached = apiCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log('Returning cached members for:', username);
    return cached.data;
  }
  
  const response = await apiClient.get(`/fetch/members/user/${username}`);
  
  // Cache the response
  apiCache.set(cacheKey, {
    data: response.data,
    timestamp: Date.now()
  });
  
  return response.data;
};

/**
 * Get committee members by conference
 * @param {string} conferenceId - The conference ID
 * Returns list of committee members
 */
export const getCommitteeByConference = async (conferenceId) => {
  // API doesn't exist yet, return empty array
  return [];
};

/**
 * Get pages/content by conference
 * @param {string} conferenceId - The conference ID
 * Returns list of pages/content
 */
export const getPagesByConference = async (conferenceId) => {
  // API doesn't exist yet, return empty array
  return [];
};

/**
 * Get workshops by conference
 * @param {string} conferenceId - The conference ID
 * Returns list of workshops
 */
export const getWorkshopsByConference = async (conferenceId) => {
  // API doesn't exist yet, return empty array
  return [];
};

/**
 * Validate discount code
 * @param {string} code - The discount code to validate
 * @param {string} conferenceId - The conference ID
 * Returns discount information or null if invalid
 */
export const validateDiscountCode = async (code, conferenceId) => {
  // API doesn't exist yet, return null (invalid code)
  return null;
};

/**
 * Submit registration
 * @param {object} registrationData - The registration data
 * Returns registration result
 */
export const submitRegistration = async (registrationData) => {
  // API doesn't exist yet, return success mock
  return { success: true, message: 'Registration submitted successfully' };
};

/**
 * Create Stripe payment intent for registration
 * @param {object} paymentData - The payment request data
 * Returns Stripe payment intent
 */
export const createStripePaymentIntent = async (paymentData) => {
  const response = await apiClient.post('/payment/stripe/register', paymentData);
  return response.data;
};

/**
 * Create PayPal payment for registration
 * @param {object} paymentData - The payment request data
 * Returns PayPal payment details
 */
export const createPaypalPayment = async (paymentData) => {
  const response = await apiClient.post('/payment/paypal/register', paymentData);
  return response.data;
};

/**
 * Submit abstract
 * @param {object} abstractData - The abstract data
 * Returns submission result
 */
export const submitAbstract = async (formData) => {
  const response = await apiClient.post('/abstract/submit', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Submit contact form
 * @param {object} contactData - The contact form data
 * Returns submission result
 */
export const submitContact = async (contactData) => {
  const response = await apiClient.post('/contact-us', contactData);
  return response.data;
};

/**
 * Request brochure download
 * @param {object} requestData - The download request data
 * Returns download result
 */
export const requestBrochureDownload = async (requestData) => {
  // API doesn't exist yet, return success mock
  return { success: true, message: 'Brochure download requested successfully' };
};

// ==================== EXTERNAL API INTEGRATION ====================

/**
 * Get conference login details by submitting conference URL
 * @param {string} conferenceUrl - The conference URL to submit
 * Returns conference configuration including email, username, ConfName, conference_url, etc.
 */
export const getConferenceLoginDetailsByUrl = async (conferenceUrl) => {
  const response = await apiClient.post('/fetch/login-details/conference-url/', {
    url: conferenceUrl
  });
  return response.data;
};

/**
 * Get important conference details by shortname
 * @param {string} shortName - The conference shortname/username
 * Returns detailed conference information including title, venue, dates, deadlines, social links, etc.
 */
export const getImportantDetailsByShortName = async (shortName) => {
  console.log('Calling getImportantDetailsByShortName with shortName:', shortName);
  
  const cacheKey = `important_details_${shortName}`;
  
  // Check cache first
  const cached = apiCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log('Returning cached important details for:', shortName);
    return cached.data;
  }
  
  const response = await apiClient.get(`/fetch/important-details/shortname/${shortName}`);
  
  // Cache the response
  apiCache.set(cacheKey, {
    data: response.data,
    timestamp: Date.now()
  });
  
  return response.data;
};

/**
 * Get conference login details from local backend API
 * Returns conference configuration including email, username, ConfName, conference_url, etc.
 */
export const getConferenceLoginDetails = async () => {
  // Extract conference URL from current website hostname (without https://)
  const hostname = window.location.hostname;
  const conferenceUrl = hostname === 'localhost' ? 'ccai2026.com' : hostname;
  
  const cacheKey = `login_details_${conferenceUrl}`;
  
  // Check cache first
  const cached = apiCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log('Returning cached login details for:', conferenceUrl);
    return cached.data;
  }
  
  const response = await apiClient.get(`/fetch/login-details/conference-url/${conferenceUrl}`);
  
  // Cache the response
  apiCache.set(cacheKey, {
    data: response.data,
    timestamp: Date.now()
  });
  
  return response.data;
};

/**
 * Extract error message from API error
 * @param {Error} error - Error object
 */
export const getErrorMessage = (error) => {
  if (error.response) {
    // Server responded with error
    const data = error.response.data;
    if (typeof data === 'string') {
      return data;
    }
    if (data?.message) {
      return data.message;
    }
    if (data?.error) {
      return data.error;
    }
    if (typeof data === 'object') {
      // Try to extract error message from common object structures
      return JSON.stringify(data);
    }
    return 'Server error occurred';
  } else if (error.request) {
    // Request made but no response
    return 'No response from server. Please check your connection.';
  } else {
    // Error in request setup
    return error.message || 'An unexpected error occurred';
  }
};

/**
 * Get current conference short name from URL or default
 */
export const getCurrentConferenceShortName = () => {
  // Try to get from URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const confParam = urlParams.get('conf') || urlParams.get('conference');
  
  if (confParam) {
    return confParam.toUpperCase();
  }
  
  // No default - will be set by login details API
  return '';
};

// Export axios instance for custom requests
export default apiClient;
